/**
 * Enhanced Text component with consistent typography across the application
 */

import React from 'react';
import {
  Text as ChakraText,
  TextProps as ChakraTextProps,
  Heading,
  HeadingProps,
  useColorModeValue,
} from '@chakra-ui/react';
import { BaseComponentProps } from '../../types';

export interface TextProps extends BaseComponentProps, ChakraTextProps {
  /** Text variant for semantic meaning */
  variant?:
    | 'body'
    | 'caption'
    | 'subtitle'
    | 'label'
    | 'error'
    | 'success'
    | 'warning';
  /** Whether text should be truncated */
  truncate?: boolean;
  /** Number of lines before truncation (for multi-line truncation) */
  noOfLines?: number;
}

const variantStyles = {
  body: {
    fontSize: 'md',
    fontWeight: 'normal',
  },
  caption: {
    fontSize: 'sm',
    fontWeight: 'normal',
    opacity: 0.8,
  },
  subtitle: {
    fontSize: 'lg',
    fontWeight: 'medium',
  },
  label: {
    fontSize: 'sm',
    fontWeight: 'semibold',
  },
  error: {
    fontSize: 'sm',
    fontWeight: 'normal',
    color: 'red.500',
  },
  success: {
    fontSize: 'sm',
    fontWeight: 'normal',
    color: 'green.500',
  },
  warning: {
    fontSize: 'sm',
    fontWeight: 'normal',
    color: 'orange.500',
  },
};

export const Text: React.FC<TextProps> = ({
  variant = 'body',
  truncate = false,
  noOfLines,
  className,
  testId,
  children,
  ...props
}) => {
  const styles = variantStyles[variant];

  return (
    <ChakraText
      {...styles}
      {...props}
      isTruncated={truncate}
      noOfLines={noOfLines}
      className={className}
      data-testid={testId}
    >
      {children}
    </ChakraText>
  );
};

export interface PageTitleProps
  extends BaseComponentProps,
    Omit<HeadingProps, 'as' | 'size'> {
  /** Heading level */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Visual size (independent of semantic level) */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
}

export const PageTitle: React.FC<PageTitleProps> = ({
  level = 1,
  size = 'xl',
  className,
  testId,
  children,
  ...props
}) => {
  const colorScheme = useColorModeValue('gray.800', 'gray.100');

  return (
    <Heading
      as={`h${level}` as any}
      size={size}
      color={colorScheme}
      className={className}
      data-testid={testId}
      {...props}
    >
      {children}
    </Heading>
  );
};

export const SectionTitle: React.FC<PageTitleProps> = ({
  level = 2,
  size = 'lg',
  ...props
}) => {
  return <PageTitle level={level} size={size} {...props} />;
};
